// src/utils/ConsoleInterceptor.ts
import { Capacitor } from '@capacitor/core';
import { DISABLE_LOG_MASKING } from './constants';
import { CURRENT_ENV } from '../services/api';

type LogLevel = "log" | "info" | "warn" | "error";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message: any[];
}

const logs: LogEntry[] = [];
const MAX_DEPTH = 7;
const MAX_LOGS = 1000;

export const ConsoleInterceptor = {
  getLogs: () => logs,
  clearLogs: () => logs.splice(0, logs.length),
};

function maskSensitiveString(str: string): string {
  if (!str || typeof str !== 'string') return str;
  let maskedStr = str;

  // 1. JWT Tokens (must be first)
  const jwtRegex = /(eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*)/g;
  maskedStr = maskedStr.replace(jwtRegex, '[REDACTED_JWT_TOKEN]');

  // 2. Bearer tokens
  const bearerRegex = /Bearer\s+([A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*|[A-Za-z0-9-_=]+)/gi;
  maskedStr = maskedStr.replace(bearerRegex, 'Bearer [REDACTED_TOKEN]');

  // 3. Long tokens (40+ chars)
  const longTokenRegex = /\b[A-Za-z0-9_-]{40,}\b/g;
  maskedStr = maskedStr.replace(longTokenRegex, '[REDACTED_TOKEN]');

  // 4. Email addresses
  const emailRegex = /([a-zA-Z0-9._+-]+)(@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  maskedStr = maskedStr.replace(emailRegex, (match, p1, p2) => {
    if (p1.length <= 1) return `*${p2}`;
    return `${p1.charAt(0)}${'*'.repeat(Math.min(p1.length - 1, 8))}${p2}`;
  });

  // 5. Phone numbers
  const phoneRegex = /(\+?\d{1,3}[\s.-]?)?\(?\d{2,4}\)?[\s.-]?\d{2,4}[\s.-]?\d{2,4}[\s.-]?\d{0,4}/g;
  maskedStr = maskedStr.replace(phoneRegex, (match) => {
     if (match.length < 8) return match;
     const digits = match.replace(/\D/g, '');
     if (digits.length < 8) return match;
     return match.substring(0, Math.min(4, match.length)) + '****' + match.substring(Math.max(match.length - 3, 4));
  });

  // 6. SSN
  const ssnRegex = /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g;
  maskedStr = maskedStr.replace(ssnRegex, '[REDACTED_SSN]');

  // 7. IPv4
  const ipv4Regex = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
  maskedStr = maskedStr.replace(ipv4Regex, '[REDACTED_IP]');

  // 8. IPv6
  const ipv6Regex = /\b(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b/g;
  maskedStr = maskedStr.replace(ipv6Regex, '[REDACTED_IPV6]');

  // 9. Credit cards
  const ccRegex = /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g;
  maskedStr = maskedStr.replace(ccRegex, '[REDACTED_CC]');

  // 10. OTP codes
  const otpRegex = /\b(?:code|otp|verification)[\s:]+(\d{4,8})\b/gi;
  maskedStr = maskedStr.replace(otpRegex, (match, code) => {
    return match.replace(code, '[REDACTED_OTP]');
  });

  return maskedStr;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function maskData(data: any, seen = new WeakSet(), depth = 0): any {
  if (data === null || data === undefined) return data;
  
  if (depth > MAX_DEPTH) return '[EXCEEDED_DEPTH]';

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 2) {
      try {
        const parsed = JSON.parse(trimmed);
        const masked = maskData(parsed, seen, depth + 1);
        return JSON.stringify(masked, null, 2);
      } catch (e) {
        // Not JSON, continue to string masking
      }
    }
    return maskSensitiveString(data);
  }

  if (typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    if (seen.has(data)) return '[CIRCULAR_REF]';
    seen.add(data);
    return data.map(item => maskData(item, seen, depth + 1));
  }

  if (seen.has(data)) return '[CIRCULAR_REF]';
  seen.add(data);

  if (data instanceof Error) {
    const maskedErr = new Error(maskSensitiveString(data.message));
    maskedErr.stack = maskSensitiveString(data.stack || '');
    return maskedErr;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maskedObj: any = {};
  for (const key of Object.keys(data)) {
    const lowerKey = key.toLowerCase();
    
    // Auth credentials
    if (
      lowerKey.includes('password') || 
      lowerKey.includes('token') || 
      lowerKey.includes('secret') || 
      lowerKey.includes('auth') || 
      lowerKey.includes('apikey') ||
      lowerKey.includes('api_key') ||
      lowerKey === 'key' ||
      lowerKey.includes('credential') ||
      lowerKey.includes('identifier') ||
      lowerKey.includes('handset') ||
      lowerKey.includes('deviceid') ||
      lowerKey.includes('device_id') ||
      lowerKey.includes('clientid') ||
      lowerKey.includes('client_id') ||
      lowerKey.includes('clientsecret') ||
      lowerKey.includes('client_secret') ||
      lowerKey.includes('accesstoken') ||
      lowerKey.includes('access_token') ||
      lowerKey.includes('refreshtoken') ||
      lowerKey.includes('refresh_token') ||
      lowerKey.includes('idtoken') ||
      lowerKey.includes('id_token') ||
      lowerKey.includes('fcmtoken') ||
      lowerKey.includes('fcm_token') ||
      lowerKey.includes('pushtoken') ||
      lowerKey.includes('push_token') ||
      lowerKey.includes('otp') ||
      (lowerKey.includes('code') && (lowerKey.includes('verification') || lowerKey.includes('confirm')))
    ) {
      maskedObj[key] = '[REDACTED_CREDENTIAL]';
    } 
    // PHI/PII
    else if (
      lowerKey === 'ssn' ||
      lowerKey.includes('socialsecurity') ||
      lowerKey.includes('social_security') ||
      lowerKey.includes('mrn') || 
      lowerKey.includes('patientid') ||
      lowerKey.includes('patient_id') ||
      lowerKey.includes('dob') || 
      lowerKey.includes('dateofbirth') ||
      lowerKey.includes('date_of_birth') ||
      lowerKey.includes('birthdate') ||
      lowerKey.includes('birth_date') ||
      lowerKey.includes('address') ||
      lowerKey.includes('street') ||
      lowerKey.includes('zip') ||
      lowerKey.includes('zipcode') ||
      lowerKey.includes('postal') ||
      lowerKey.includes('postalcode') ||
      (lowerKey === 'name' && depth === 0) ||
      lowerKey.includes('firstname') ||
      lowerKey.includes('first_name') ||
      lowerKey.includes('lastname') ||
      lowerKey.includes('last_name') ||
      lowerKey.includes('fullname') ||
      lowerKey.includes('full_name') ||
      lowerKey.includes('givenname') ||
      lowerKey.includes('given_name') ||
      lowerKey.includes('familyname') ||
      lowerKey.includes('family_name') ||
      lowerKey.includes('middlename') ||
      lowerKey.includes('middle_name') ||
      lowerKey.includes('creditcard') ||
      lowerKey.includes('credit_card') ||
      lowerKey.includes('cardnumber') ||
      lowerKey.includes('card_number') ||
      lowerKey.includes('cvv') ||
      lowerKey.includes('cvc')
    ) {
      maskedObj[key] = '[REDACTED_PHI]';
    }
    // Contact info
    else if (
      lowerKey.includes('email') || 
      lowerKey.includes('phone') || 
      lowerKey.includes('mobile') ||
      lowerKey.includes('telephone') ||
      lowerKey.includes('contact')
    ) {
      maskedObj[key] = maskData(data[key], seen, depth + 1);
    } 
    // Location
    else if (
      lowerKey === 'latitude' ||
      lowerKey === 'longitude' ||
      lowerKey === 'lat' ||
      lowerKey === 'lng' ||
      (lowerKey === 'location' && typeof data[key] === 'string')
    ) {
      maskedObj[key] = '[REDACTED_LOCATION]';
    }
    else {
      maskedObj[key] = maskData(data[key], seen, depth + 1);
    }
  }
  return maskedObj;
}

// Silence console output only on the WEB platform when CURRENT_ENV is a
// release-like target (staging or production). Local/development always
// show logs. Native (iOS/Android) keeps its logs so Xcode/logcat remain
// useful during native debugging.
const SILENCED_ENVS: ReadonlySet<string> = new Set(['production', 'staging']);
const IS_RELEASE_WEB = Capacitor.getPlatform() === 'web' && SILENCED_ENVS.has(CURRENT_ENV as string);
const SILENT_LEVELS: ReadonlySet<LogLevel> = new Set<LogLevel>(['log', 'info']);


(function interceptConsole() {
  if (DISABLE_LOG_MASKING) {
    console.info('⚠️  ConsoleInterceptor: log masking DISABLED. Set DISABLE_LOG_MASKING=false in constants.ts before committing.');
    return;
  }

  (["log", "info", "warn", "error"] as LogLevel[]).forEach((level) => {
    const original = console[level];
    const skipForwardingToConsole = IS_RELEASE_WEB && SILENT_LEVELS.has(level);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console[level] = (...args: any[]) => {

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const maskedArgs = args.map((arg: any, index: number) => {
         try {
           if (typeof arg === 'string' && index > 0) {
             const prevArg = args[index - 1];
             if (typeof prevArg === 'string') {
               const lowerPrev = prevArg.toLowerCase();
               if (
                 lowerPrev.includes('token') ||
                 lowerPrev.includes('password') ||
                 lowerPrev.includes('key') ||
                 lowerPrev.includes('secret') ||
                 lowerPrev.includes('patient') ||
                 lowerPrev.includes('mrn') ||
                 lowerPrev.includes('identifier') ||
                 lowerPrev.includes('device') ||
                 lowerPrev.includes('handset') ||
                 lowerPrev.includes('otp') ||
                 lowerPrev.includes('code') ||
                 lowerPrev.includes('credential') ||
                 lowerPrev.includes('auth response') ||
                 lowerPrev.includes('response') ||
                 lowerPrev.includes('user data') ||
                 lowerPrev.includes('graph api')
               ) {
                 return '[REDACTED_CONTEXT_DATA]';
               }
             }
           }
           return maskData(arg);
         } catch (err) {
           return '[Error formatting object for console]';
         }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const processedArgsForBuffer = maskedArgs.map((arg: any) => {
        if (typeof arg === "object" && arg !== null) {
          try {
            return JSON.stringify(arg);
          } catch {
            return "[Non-serializable object]";
          }
        }
        return arg;
      });

      logs.push({
        level,
        message: processedArgsForBuffer,
        timestamp: new Date().toISOString(),
      });

      if (logs.length > MAX_LOGS) {
        logs.splice(0, logs.length - MAX_LOGS);
      }

      if (skipForwardingToConsole) return;


      original.apply(console, maskedArgs);
    };
  });
})();
